import { withApiBaseUrl } from '../lib/apiBaseUrl'
import authService from './authService'
import { handleAuthExpired } from '../utils/sessionReplacedHandler'

export type PointsRule = {
  id: string
  name: string
  inputAmount: number
  outputAmount: number
  enabled: boolean
  displayOrder: number
}

export type RewardType = 'credit' | 'item'

export type PointsReward = {
  id: string
  name: string
  description: string
  imageUrl: string
  type: RewardType
  pointsCost: number
  creditAmount: number
  stock: number
  limitPerUserPerDay: number
  limitPerDay: number
  dailyCreditCap: number
  enabled: boolean
  displayOrder: number
}

export type PointsSystemConfig = {
  cashToPointsEnabled: boolean
  pointsToCashEnabled: boolean
  depositToPointsAutoEnabled: boolean
  cashToPointsRatePercent: number
  pointsToCashRatePercent: number
  depositToPointsRatePercent: number
  rewardsEnabled?: boolean
}

export type PointsConfigData = {
  tenantId: string
  system: PointsSystemConfig
  cashToPointsRules: PointsRule[]
  pointsToCashRules: PointsRule[]
  rewards: PointsReward[]
  version: number
}

type PointsConfigResponse = {
  success: boolean
  data?: PointsConfigData
  message?: string
}

export type ExchangeDirection = 'cash-to-points' | 'points-to-cash'

type ExchangePointsResponse = {
  success: boolean
  data?: {
    ruleId: string
    ruleName: string
    cashAmount: number
    pointsAmount: number
    currentPoints: number
    pointsEarned: number
    walletBalance: number
  }
  message?: string
}

type RedeemRewardResponse = {
  success: boolean
  data?: {
    redemptionId: string
    rewardId: string
    rewardName: string
    rewardType: RewardType
    pointsCost: number
    creditAmount: number
    currentPoints: number
    walletBalance: number | null
    fulfillmentStatus: string | null
  }
  message?: string
}

export const USER_SUMMARY_UPDATED_EVENT = 'user_summary_updated'

const sortRules = (left: PointsRule, right: PointsRule) =>
  Number(left.displayOrder || 0) - Number(right.displayOrder || 0)

const normalizeRule = (rule: Partial<PointsRule> | null | undefined): PointsRule | null => {
  if (!rule?.id) return null
  return {
    id: String(rule.id),
    name: String(rule.name || ''),
    inputAmount: Number(rule.inputAmount || 0),
    outputAmount: Number(rule.outputAmount || 0),
    enabled: rule.enabled !== false,
    displayOrder: Number(rule.displayOrder || 0),
  }
}

const normalizeReward = (reward: Partial<PointsReward> | null | undefined): PointsReward | null => {
  if (!reward?.id) return null
  const type = reward.type === 'credit' ? 'credit' : 'item'
  return {
    id: String(reward.id),
    name: String(reward.name || ''),
    description: String(reward.description || ''),
    imageUrl: String(reward.imageUrl || ''),
    type,
    pointsCost: Number(reward.pointsCost || 0),
    creditAmount: type === 'credit' ? Number(reward.creditAmount || 0) : 0,
    stock: Number(reward.stock ?? -1),
    limitPerUserPerDay: Number(reward.limitPerUserPerDay || 0),
    limitPerDay: Number(reward.limitPerDay || 0),
    dailyCreditCap: Number(reward.dailyCreditCap || 0),
    enabled: reward.enabled !== false,
    displayOrder: Number(reward.displayOrder || 0),
  }
}

const normalizeConfig = (config: PointsConfigData | null | undefined, tenantId: string): PointsConfigData => ({
  tenantId: config?.tenantId || tenantId,
  system: {
    cashToPointsEnabled: config?.system?.cashToPointsEnabled === true,
    pointsToCashEnabled: config?.system?.pointsToCashEnabled === true,
    depositToPointsAutoEnabled: config?.system?.depositToPointsAutoEnabled === true,
    cashToPointsRatePercent: Number(config?.system?.cashToPointsRatePercent || 0),
    pointsToCashRatePercent: Number(config?.system?.pointsToCashRatePercent || 0),
    depositToPointsRatePercent: Number(config?.system?.depositToPointsRatePercent || 0),
    rewardsEnabled: config?.system?.rewardsEnabled !== false,
  },
  cashToPointsRules: Array.isArray(config?.cashToPointsRules)
    ? config!.cashToPointsRules.map(normalizeRule).filter(Boolean) as PointsRule[]
    : [],
  pointsToCashRules: Array.isArray(config?.pointsToCashRules)
    ? config!.pointsToCashRules.map(normalizeRule).filter(Boolean) as PointsRule[]
    : [],
  rewards: Array.isArray(config?.rewards)
    ? config!.rewards.map(normalizeReward).filter(Boolean) as PointsReward[]
    : [],
  version: Number(config?.version || 0),
})

const getAuthHeaders = (tenantId: string) => {
  const token = authService.getAccessToken()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
    'X-Requested-With': 'XMLHttpRequest',
  }
}

const readJson = async <T>(response: Response): Promise<T> =>
  (await response.json().catch(async () => ({ message: await response.text().catch(() => '') }))) as T

export const getActivePointsRules = (rules: PointsRule[] | undefined): PointsRule[] =>
  [...(rules || [])]
    .filter((rule) => rule.enabled !== false && Number(rule.inputAmount) > 0 && Number(rule.outputAmount) > 0)
    .sort(sortRules)

export const getActiveRewards = (rewards: PointsReward[] | undefined): PointsReward[] =>
  [...(rewards || [])]
    .filter((reward) => reward.enabled !== false && Number(reward.pointsCost) > 0)
    .sort((left, right) => Number(left.displayOrder || 0) - Number(right.displayOrder || 0))

export async function getPointsConfig(tenantId: string): Promise<PointsConfigData | null> {
  try {
    const response = await fetch(
      withApiBaseUrl(`/api/v1/configuration-service/points-config/${encodeURIComponent(tenantId)}`),
      {
        method: 'GET',
        headers: getAuthHeaders(tenantId),
        credentials: 'include',
      }
    )
    const payload = await readJson<PointsConfigResponse>(response)
    if (response.status === 401 || response.status === 403) {
      const message = String(payload?.message || '').toLowerCase()
      if (response.status === 401 || message.includes('invalid token') || message.includes('expired token')) {
        await handleAuthExpired()
        return null
      }
    }
    if (!response.ok || !payload.success || !payload.data) {
      return null
    }
    return normalizeConfig(payload.data, tenantId)
  } catch (error) {
    console.error('Failed to load points config:', error)
    return null
  }
}

export async function exchangePoints(params: {
  tenantId: string
  userId: string
  direction: ExchangeDirection
  ruleId: string
}): Promise<ExchangePointsResponse> {
  const response = await fetch(
    withApiBaseUrl(`/api/v1/user-info/${encodeURIComponent(params.tenantId)}/${encodeURIComponent(params.userId)}/points/exchange`),
    {
      method: 'POST',
      headers: getAuthHeaders(params.tenantId),
      credentials: 'include',
      body: JSON.stringify({
        direction: params.direction,
        ruleId: params.ruleId,
      }),
    }
  )

  const payload = await readJson<ExchangePointsResponse>(response)
  if (response.status === 401 || response.status === 403) {
    const message = String(payload?.message || '').toLowerCase()
    if (response.status === 401 || message.includes('invalid token') || message.includes('expired token')) {
      await handleAuthExpired()
      throw new Error('AUTH_EXPIRED')
    }
  }
  if (!response.ok || !payload.success) {
    throw new Error(payload.message || 'ไม่สามารถแลกเงินเป็นคะแนนได้')
  }
  return payload
}

export async function redeemReward(params: {
  tenantId: string
  userId: string
  rewardId: string
  shippingInfo?: Record<string, unknown> | null
}): Promise<RedeemRewardResponse> {
  const response = await fetch(
    withApiBaseUrl(`/api/v1/user-info/${encodeURIComponent(params.tenantId)}/${encodeURIComponent(params.userId)}/rewards/redeem`),
    {
      method: 'POST',
      headers: getAuthHeaders(params.tenantId),
      credentials: 'include',
      body: JSON.stringify({
        rewardId: params.rewardId,
        shippingInfo: params.shippingInfo || null,
      }),
    }
  )

  const payload = await readJson<RedeemRewardResponse>(response)
  if (response.status === 401 || response.status === 403) {
    const message = String(payload?.message || '').toLowerCase()
    if (response.status === 401 || message.includes('invalid token') || message.includes('expired token')) {
      await handleAuthExpired()
      throw new Error('AUTH_EXPIRED')
    }
  }
  if (!response.ok || !payload.success) {
    throw new Error(payload.message || 'ไม่สามารถแลกรางวัลได้')
  }
  return payload
}

export const notifyUserSummaryUpdated = () => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(USER_SUMMARY_UPDATED_EVENT))
  window.dispatchEvent(new Event('balance_notification'))
}
